import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Bell, Check, CheckCheck } from "lucide-react";
import { api, unwrap } from "@/lib/api";
import { authFetch } from "@/lib/auth-fetch";
import { useAuthStore } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Drawer, DrawerHeader, DrawerBody, DrawerFooter } from "./Drawer";
import { EmptyState } from "./EmptyState";

/**
 * Pencil component `b1bzW` (`Notifications Center`).
 *
 * Right-rail drawer opened by the bell button in `TopBar`. Lists the
 * operator's notifications grouped by date bucket (Today / Yesterday /
 * Earlier), with an unread dot per item and a "Mark all read" footer action.
 */

export function NotificationsDrawer({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const accessToken = useAuthStore((s) => s.accessToken);
  const qc = useQueryClient();

  const feed = useQuery({
    queryKey: ["notifications"],
    enabled: !!accessToken && open,
    queryFn: async () => unwrap(await api.GET("/api/v1/notifications", {})),
    refetchInterval: open ? 30_000 : false,
  });

  const markRead = useMutation({
    mutationFn: async (id: string) =>
      unwrap(
        await api.POST("/api/v1/notifications/{id}/read", {
          params: { path: { id } },
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });

  const items = feed.data?.items ?? [];
  const unreadIds = useMemo(
    () => items.filter((i) => !i.readAt).map((i) => i.id!),
    [items],
  );

  const grouped = useMemo(() => groupByDay(items), [items]);

  const markAll = async () => {
    await Promise.all(
      unreadIds.map((id) =>
        authFetch(`/api/v1/notifications/${id}/read`, { method: "POST" }).catch(
          () => {},
        ),
      ),
    );
    qc.invalidateQueries({ queryKey: ["notifications"] });
  };

  return (
    <Drawer open={open} onClose={onClose} width="md">
      <DrawerHeader
        title="Notifications"
        subtitle={
          feed.data
            ? `${feed.data.unreadCount ?? 0} unread`
            : feed.isLoading
              ? "Loading…"
              : undefined
        }
        onClose={onClose}
      />

      <DrawerBody>
        {feed.isError && (
          <p className="px-5 py-4 text-sm text-destructive">
            {(feed.error as Error).message}
          </p>
        )}

        {!feed.isError && items.length === 0 && !feed.isLoading && (
          <div className="px-5 py-8">
            <EmptyState
              icon={Bell}
              title="You're all caught up"
              description="Booking events, payment receipts, and inventory alerts will show up here."
            />
          </div>
        )}

        {grouped.map((group) => (
          <div key={group.label}>
            <p className="sticky top-0 z-10 bg-background/95 backdrop-blur px-5 py-2 text-[10px] uppercase tracking-wider text-muted-foreground font-medium border-b border-border">
              {group.label}
            </p>
            <ul>
              {group.items.map((n) => {
                const unread = !n.readAt;
                return (
                  <li
                    key={n.id}
                    className={`group flex items-start gap-3 px-5 py-3 border-b border-border last:border-0 ${
                      unread ? "bg-primary-light/40" : "hover:bg-muted/30"
                    }`}
                  >
                    <span
                      className={`mt-1.5 h-2 w-2 rounded-full shrink-0 ${
                        unread ? "bg-primary" : "bg-transparent border border-border"
                      }`}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium leading-tight">{n.title}</p>
                      {n.body && (
                        <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                          {n.body}
                        </p>
                      )}
                      <p className="text-[11px] text-muted-foreground mt-1">
                        {n.createdAt && timeAgo(n.createdAt)}
                      </p>
                    </div>
                    {unread && (
                      <button
                        type="button"
                        onClick={() => markRead.mutate(n.id!)}
                        className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-foreground transition"
                        aria-label="Mark as read"
                        title="Mark as read"
                      >
                        <Check className="h-4 w-4" />
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </DrawerBody>

      <DrawerFooter className="justify-between">
        <p className="text-xs text-muted-foreground">
          Updates every 30 seconds while open.
        </p>
        <Button
          size="sm"
          variant="outline"
          disabled={unreadIds.length === 0 || markRead.isPending}
          onClick={markAll}
        >
          <CheckCheck className="h-3.5 w-3.5 mr-1.5" />
          Mark all read
        </Button>
      </DrawerFooter>
    </Drawer>
  );
}

function groupByDay<T extends { createdAt?: string }>(
  items: T[],
): { label: string; items: T[] }[] {
  if (items.length === 0) return [];
  const today = new Date().toDateString();
  const yesterday = new Date(Date.now() - 86_400_000).toDateString();

  const buckets: Record<string, T[]> = {};
  for (const it of items) {
    const d = it.createdAt ? new Date(it.createdAt).toDateString() : "Unknown";
    const label =
      d === today
        ? "Today"
        : d === yesterday
          ? "Yesterday"
          : new Date(it.createdAt!).toLocaleDateString(undefined, {
              month: "short",
              day: "numeric",
              year: "numeric",
            });
    (buckets[label] ??= []).push(it);
  }
  // Order: Today → Yesterday → newest first by date.
  const labels = Object.keys(buckets);
  labels.sort((a, b) => {
    if (a === "Today") return -1;
    if (b === "Today") return 1;
    if (a === "Yesterday") return -1;
    if (b === "Yesterday") return 1;
    return new Date(b).getTime() - new Date(a).getTime();
  });
  return labels.map((label) => ({ label, items: buckets[label] }));
}

function timeAgo(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.round(ms / 60_000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}
